import { Info } from "@phosphor-icons/react";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import type { Tone } from "@/tokens";
import { WarningAlertIcon, warningSurfaceTextStyle } from "../warningVisual";

export type InfoCardProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  tone?: Tone;
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
};

/* Exact compiled output of the pre-migration Tailwind utilities. */

const bgText = (bg: string, text: string): CSSObject => ({
  "--tw-bg-opacity": "1",
  backgroundColor: `hsl(var(--bfrs-color-${bg}) / var(--tw-bg-opacity, 1))`,
  "--tw-text-opacity": "1",
  color: `hsl(var(--bfrs-color-${text}) / var(--tw-text-opacity, 1))`
});

const infoCardToneStyles: Record<Tone, CSSObject> = {
  neutral: bgText("surface-muted", "text-secondary"),
  primary: bgText("primary-soft", "primary"),
  brand: bgText("primary-soft", "primary"),
  success: bgText("success-soft", "success-contrast"),
  warning: {},
  danger: bgText("danger-soft", "danger-contrast"),
  info: bgText("info-soft", "info-contrast")
};

const InfoCardRoot = styled("div", {
  label: "AgenticInfoCard-root"
})({
  display: "flex",
  alignItems: "flex-start",
  gap: ".75rem",
  borderRadius: "var(--bfrs-radius-xl)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  padding: "1rem"
});

const InfoCardIconBubble = styled("span", {
  label: "AgenticInfoCard-iconBubble",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { tone: Tone } }>(({ ownerState }) => [
  {
    display: "flex",
    width: "2.25rem",
    height: "2.25rem",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "var(--bfrs-radius-lg)"
  },
  infoCardToneStyles[ownerState.tone]
]);

const InfoCardContent = styled("span", {
  label: "AgenticInfoCard-content"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

const InfoCardTitle = styled("span", {
  label: "AgenticInfoCard-title"
})({
  display: "block",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const InfoCardDescription = styled("span", {
  label: "AgenticInfoCard-description"
})({
  marginTop: ".25rem",
  display: "block",
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const InfoCardActions = styled("span", {
  label: "AgenticInfoCard-actions"
})({
  flexShrink: 0
});

export const InfoCard = forwardRef<HTMLDivElement, InfoCardProps>(({ className, tone = "info", icon, title, description, actions, children, ...props }, ref) => (
  <InfoCardRoot ref={ref} className={className} {...props}>
    <InfoCardIconBubble ownerState={{ tone }} style={tone === "warning" ? warningSurfaceTextStyle : undefined}>
      {icon ?? (tone === "warning" ? <WarningAlertIcon /> : <Info size={18} weight="bold" />)}
    </InfoCardIconBubble>
    <InfoCardContent>
      <InfoCardTitle>{title}</InfoCardTitle>
      {description && <InfoCardDescription>{description}</InfoCardDescription>}
      {children}
    </InfoCardContent>
    {actions && <InfoCardActions>{actions}</InfoCardActions>}
  </InfoCardRoot>
));

InfoCard.displayName = "InfoCard";
